// src/pages/ApprovalQueuePage.tsx
//
// Every bill awaiting a decision — at EITHER level — with the actions this
// officer can actually perform on it, and a sentence for every one they cannot.
//
// ══ WHAT CHANGED, AND WHY EACH PART OF IT ══
//
// **I9 — `AWAITING_L2` was invisible.** This page filtered to
// `{SUBMITTED, AWAITING_L1}`, so a bill that had cleared level 1 appeared in no
// queue and in no dashboard count. `approve-l2` — the headline addition of this
// branch — had no entry point anywhere in the product: the only people who could
// action those bills could not see them.
//
// **I10 — Reject was hard-disabled** behind `title="Reject is not implemented in
// this slice — the gateway has no reject route yet"`. That route exists, and
// `api.rejectBill` calls it. The reason is mandatory at the API, so it is
// collected here rather than discovered as a 400 (`lib/reject-reason.ts` holds
// the bound, read from the gateway's own constant).
//
// **I7 — the buttons gated on status alone.** Verified live as an HSP_USER with
// approval level 0: every queued bill offered "Approve", and every press came
// back 403 `APPROVE_L1 requires approval level 1`. Availability now comes from
// `lib/authority.ts`, which asks `authorise()` — the gateway's own function —
// the same question the server will ask. **That is a courtesy, not a control:**
// every route still authorises independently, and the server's refusal is still
// what this page renders when one arrives.
//
// The two sections are the point. A bill nobody in this scope can action is
// still SHOWN — hiding it is how I9 happened — but it is shown under a heading
// that says so, with the reason, rather than sitting beside actionable work
// wearing buttons that do not work.
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { SgdsButton, SgdsModal, SgdsTextarea } from '@govtechsg/sgds-web-component/react'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { sortParams, useSort } from '../lib/useSort'
import { SortAnnouncement } from '../components/SortableTh'
import { useAuth } from '../state/AuthContext'
import { computeUnavailabilitySentence, pendingDecision, unavailabilitySentence, type BillAuthority } from '../lib/authority'
import { assessQueue, needsSplitRefusal, queueSections, type QueueAssessment } from '../lib/queue'
import {
  isSendableRejectReason,
  officerCommentHint,
  rejectReasonHint,
  rejectReasonMessage,
  rejectReasonProblem,
} from '../lib/reject-reason'
import { publishedMaxLength } from '../lib/types'
import { BillStatusBadge } from '../components/StatusBadge'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { Money } from '../components/Money'
import { billTypeLabel, formatCents, formatIsoDate } from '../lib/format'
import { targetValue } from '../lib/sgds-dom'
import type { Bill, Scope } from '../lib/types'

type PendingAction = 'APPROVE' | 'REJECT'

interface Pending {
  readonly bill: Bill
  readonly action: PendingAction
  readonly level: 1 | 2
  readonly reason: string
  /**
   * ★★ WAVE 9 — Annex A 37/38's approval comment, OPTIONAL on every decision.
   *
   * ⚠ Frozen into `Pending` with the rest of the decision rather than read live at
   * submit time, which is the discipline `ReferenceDataPage`'s `PendingWrite` records
   * at length: an edit made while the confirmation is open must not change what is
   * sent, or the officer confirms one thing and applies another.
   */
  readonly comment: string
  readonly submitting: boolean
  readonly error: string | null
}

interface Outcome {
  readonly reference: string
  readonly sentence: string
}

/**
 * ★ TASK 5D. `GET /api/bills` is now paginated (P2C3 §7.1.6) — see
 * `DashboardPage.tsx`'s own `BILLS_SNAPSHOT_LIMIT` for the identical
 * reasoning. This page's header claims "every bill in your scope still
 * awaiting a decision"; the banner below is what keeps that claim honest once
 * a scope holds more bills than one page can safely carry, rather than
 * silently truncating a queue named as complete.
 *
 * ⚠ ★★ WAVE 8 (Task 2, S-029) — A BANNER BESIDE A FALSE SENTENCE IS NOT ENOUGH,
 * which is what that paragraph had settled for. The header, and the empty state
 * under it, now make the completeness claim ONLY when `total === loadedBills.length`
 * proves it, and narrow to the loaded page otherwise. The limit itself is
 * unchanged and deliberately so: raising it would move the same defect further out
 * and cost the P2C3 §7.1.6 byte budget `MAX_BILL_LIMIT` was measured against.
 */
const BILLS_SNAPSHOT_LIMIT = 60

/**
 * ★★★ WAVE 24 — THE ORDER THIS QUEUE IS READ IN, AND WHY IT IS THE SERVER'S.
 *
 * This page had NO ordering control of any kind, on the most operationally
 * important list in the product. The standing rule on this estate is that every
 * data list can be ordered by the reader, and its own note says the surfaces
 * that get missed are always the ones the builder judged incidental — this one
 * was missed because it renders as grouped CARDS rather than a `<table>`, so the
 * mechanised header census (`sortable-header-census.test.ts`) could not see it
 * and no human had reason to look.
 *
 * ⚠★ **The cap is what makes this a correctness matter rather than a
 * convenience.** The page reads at most {@link BILLS_SNAPSHOT_LIMIT} rows of a
 * scope the demonstration corpus fills with over a thousand pending bills. So
 * the order does not merely arrange what is on screen — it decides WHICH bills
 * are on screen at all. An officer who wants to clear the largest exposures
 * first could not reach them: they were somewhere in the 960 rows the cap
 * dropped, and nothing on the page offered a way to bring them into it.
 *
 * ⚠ Sorting in the browser would have been the wrong fix and a plausible one.
 * It reorders the 60 already fetched and leaves the other 960 exactly as
 * unreachable, while making the screen look like it answered the question. The
 * order is therefore sent to the SERVER, which is also the rule this estate
 * already holds for any capped or paged list.
 *
 * The keys are `BILL_SORT_COLUMNS`' own — the gateway validates against that
 * whitelist and answers 400 for anything else, so a typo here fails loudly.
 */
const QUEUE_ORDER_LABELS = {
  amount: 'Amount',
  serviceDate: 'Service date',
  reference: 'Reference',
  agencyCode: 'Agency',
  status: 'Status',
} as const

type QueueOrderKey = keyof typeof QUEUE_ORDER_LABELS

export function ApprovalQueuePage() {
  const { user } = useAuth()
  const { sort, onSort, interacted } = useSort<QueueOrderKey>()
  const bills = useApi(
    () => api.bills({ limit: BILLS_SNAPSHOT_LIMIT, ...sortParams(sort) }),
    [sort.column, sort.dir],
  )
  const [pending, setPending] = useState<Pending | null>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const scope: Scope | null = user?.scope ?? null

  // ⚠ Filtered through `pendingDecision()` rather than a status set written out
  // here. The old page had its own `AWAITING_L1_STATUSES = {SUBMITTED,
  // AWAITING_L1}` literal, which is exactly how `AWAITING_L2` came to be missing
  // from the queue while the gateway grew a route for it (I9). There is now one
  // definition of "a decision is outstanding", in `lib/authority.ts`, and both
  // the queue membership and the button gate read it.
  /**
   * ★★ S-067 — the reason ceiling, as the SERVER published it on the read this
   * screen already makes. `undefined` until the first response arrives, and
   * `undefined` for ever against a gateway that publishes none: the field then shows
   * no ceiling and the server's refusal is the only bound, which is safe. A
   * remembered 1,000 here would be the finding this change closes.
   */
  const reasonMaxLength = publishedMaxLength(bills.data?.validation, 'reason')
  /** ★★ WAVE 9 — the same, for Annex A 37/38's approval comment. */
  const commentMaxLength = publishedMaxLength(bills.data?.validation, 'comment')

  const loadedBills = bills.data?.bills ?? []
  const queue = loadedBills.filter((b) => pendingDecision(b.status) !== null)
  // ★ TASK 5D — this page's own claim is "every bill in your scope still
  // awaiting a decision", and `loadedBills` is now at most one page
  // (`BILLS_SNAPSHOT_LIMIT`) of a scope that can hold many more. A scope this
  // large is exactly the corpus that exposed the original defect (Task 5c: 40
  // bills hid it, 5,000 did not) — flagged rather than silently truncated.
  const total = bills.data?.total ?? 0
  const queueMayBeIncomplete = total > loadedBills.length
  /**
   * ★★ WAVE 8 (Task 2) — has this page PROVED it read the whole scope?
   *
   * ⚠ **`!queueMayBeIncomplete` is not the same question, and the difference was
   * found by running it.** Before the response arrives, `total` and
   * `loadedBills.length` are both `0`, so `total > loadedBills.length` is false and
   * a header keyed on it printed "every bill in your scope still awaiting a
   * decision" over a page that had read nothing at all. That is the strong claim
   * made in the one state where it is least supported — a fail-open, and exactly
   * the "distinguish not built from no data" shape this repository has logged. The
   * claim now requires the response to exist AND to prove completeness; anything
   * else gets a sentence with no completeness in it.
   */
  const queueProvenComplete = bills.data !== undefined && total === loadedBills.length

  // ⚠ The three-way split lives in `lib/queue.ts`, not here, and it grew a
  // condition in the move: "Waiting on a pro-ration split" tells the officer to
  // go and record one, so a bill only belongs there if this officer can. That
  // used to be true by coincidence — of `REJECT` carrying the same authority as
  // the approval beside it, and of an ordinary compute being level 0 and ceiling
  // exempt — rather than because anything asked. See that module's header, and
  // `queue.test.ts`, which pins the coincidence so a divergence fails a test
  // instead of putting a false instruction on this screen.
  const assessed: readonly QueueAssessment[] = scope === null ? [] : assessQueue(scope, queue)
  const { mine, blockedOnCompute, theirs } = queueSections(assessed)

  const openAction = (bill: Bill, action: PendingAction, level: 1 | 2) =>
    setPending({ bill, action, level, reason: '',
      comment: '', submitting: false, error: null })
  const closeAction = () => setPending(null)
  // The modal keeps its last words while it closes: changing the header in the same update that closes it made
  // sgds-modal schedule a second update after the first completed (UAT N-11).
  const shownRef = useRef<{ action: string; level: number }>({ action: 'APPROVE', level: 1 })
  if (pending !== null) shownRef.current = { action: pending.action, level: pending.level }
  const shown = shownRef.current

  const submit = async () => {
    if (pending === null) return
    if (pending.action === 'REJECT' && !isSendableRejectReason(pending.reason, reasonMaxLength)) return

    setPending({ ...pending, submitting: true, error: null })
    try {
      if (pending.action === 'REJECT') {
        await api.rejectBill(pending.bill.reference, pending.reason.trim(), pending.comment.trim())
        setOutcome({
          reference: pending.bill.reference,
          sentence: 'rejected. The reason you gave is now on the permanent transition ledger and is what the provider will be told.',
        })
      } else if (pending.level === 2) {
        await api.approveLevel2Bill(pending.bill.reference, pending.comment.trim())
        setOutcome({ reference: pending.bill.reference, sentence: 'approved at level 2. It is now APPROVED: the lifecycle is complete.' })
      } else {
        await api.approveBill(pending.bill.reference, pending.comment.trim())
        setOutcome({ reference: pending.bill.reference, sentence: 'approved at level 1 and moved to level 2.' })
      }
      setPending(null)
      bills.reload()
    } catch (error) {
      setPending({
        ...pending,
        submitting: false,
        // The server's own refusal, verbatim. The client-side gate above is a
        // courtesy; THIS is the authority, and it is never swallowed or
        // replaced with a generic failure string.
        error: error instanceof ApiError ? error.message : 'Could not record that decision.',
      })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Approval queue</h1>
        {/*
          ⚠ ★★ WAVE 8 (Task 2, S-029) — THE COMPLETENESS CLAIM IS NOW CONDITIONAL,
          because it was only ever true some of the time. "Every bill in your scope
          still awaiting a decision" was printed unconditionally over `loadedBills`,
          which is at most `BILLS_SNAPSHOT_LIMIT` rows of a scope that the seeded
          corpus fills with 1,020 pending bills — so on the population this product
          is demonstrated against, the sentence was false and the banner below was
          the only thing saying so. It is now claimed ONLY when the page really did
          read the whole scope (`total === loadedBills.length`), and narrowed to
          what it can prove otherwise. The claim is not deleted: on the ordinary
          small scope it is true, and a screen that hedges when it does not need to
          teaches a reader to discount everything else it says.
        */}
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          {queueProvenComplete
            ? 'Every bill in your scope still awaiting a decision, at level 1 or level 2.'
            : queueMayBeIncomplete
              ? `The bills awaiting a decision, at level 1 or level 2, among the first ${loadedBills.length} of the ${total} bills in your scope: see the note below for the rest.`
              : 'The bills your scope can see that are still awaiting a decision, at level 1 or level 2.'}{' '}
          Which buttons appear depends on your own approval grants (agency, bill type, level and monetary
          limit), so this page offers only what PRS2.0 will accept. PRS2.0 checks again regardless,
          and its refusal is shown here word for word if one arrives.
        </p>
        {/*
          ★★★ The ordering control. Buttons rather than `<SortableTh>` because
          there is no table here to put a header on — the queue is grouped by
          WHY a bill can or cannot be actioned, and that grouping is the page's
          argument, not an arrangement to be sorted away.

          ⚠ `aria-pressed`, not `aria-sort`. `aria-sort` belongs on a
          `columnheader`; on a free-standing button it is ignored, and shipping
          an attribute a screen reader drops would look like accessibility while
          providing none. The live region below is what actually announces the
          change, and it is the same component the sortable tables use.
        */}
        <div className="mt-4 flex items-center gap-2 flex-wrap" role="group" aria-label="Order the queue">
          <span className="text-sm text-[var(--sgds-body-color-subtle)]">Order by</span>
          {(Object.keys(QUEUE_ORDER_LABELS) as readonly QueueOrderKey[]).map((key) => {
            const active = sort.column === key
            return (
              <button
                key={key}
                type="button"
                className="pt-order-chip"
                aria-pressed={active}
                onClick={() => onSort(key)}
              >
                {QUEUE_ORDER_LABELS[key]}
                {active ? (sort.dir === 'asc' ? ' \u2191' : ' \u2193') : ''}
              </button>
            )
          })}
        </div>
        <SortAnnouncement
          sort={sort}
          interacted={interacted}
          busy={bills.loading}
          columnLabels={QUEUE_ORDER_LABELS}
          rowCount={queue.length}
        />
        {/*
          ⚠ Said on the screen, not only in a comment. An officer choosing an
          order needs to know it changes WHICH bills the cap keeps, or they will
          read a reordered page as the same population rearranged.
        */}
        {queueMayBeIncomplete && (
          <p className="text-sm text-[var(--sgds-body-color-subtle)] mt-2 max-w-[70ch]">
            The order is applied by the server across all {total} bills in your scope, then the first{' '}
            {BILLS_SNAPSHOT_LIMIT} are read, so changing it changes which bills appear here, not just their
            arrangement.
          </p>
        )}

        <p className="text-sm text-[var(--sgds-color-subtle)] mt-2 max-w-[70ch]">
          There is no <strong>Refer</strong> action. PSD has not yet confirmed whether its process has a third
          outcome besides approve and reject, so there is no button for it.
        </p>
      </div>

      {/*
        ★ TASK 5D. This page's own opening sentence claims completeness —
        "every bill in your scope" — and `loadedBills` is now bounded at
        `BILLS_SNAPSHOT_LIMIT` rows of a scope `total` can exceed. Stated
        rather than silently truncated: a queue that drops bills off the
        bottom without saying so is worse than one that says it might have.
      */}
      {queueMayBeIncomplete && (
        <div className="pt-well p-4" role="status">
          <p className="text-sm">
            Showing the first {loadedBills.length} of {total} bills in your scope, in the server&rsquo;s default
            order, not every bill awaiting a decision may be listed below. Use the{' '}
            <Link to="/bills" className="pt-ref-link">
              Bills
            </Link>{' '}
            list, sorted by Status, to page through the rest.
          </p>
        </div>
      )}

      {outcome !== null && (
        <div className="pt-card" role="status">
          <p className="font-semibold text-[var(--sgds-success-color-emphasis)]">
            <span className="pt-mono">{outcome.reference}</span> {outcome.sentence}
          </p>
        </div>
      )}

      {bills.error !== null ? (
        <ErrorCard title="Could not load the approval queue" message={bills.error} onRetry={bills.reload} />
      ) : bills.initialLoading ? (
        <LoadingCard label="Loading approval queue" rows={5} />
      ) : assessed.length === 0 ? (
        <EmptyCard
          // ⚠ ★★ WAVE 8 (Task 2) — the SAME narrowing as the header above, and this
          // one mattered more: "no bill in your scope" over an empty PAGE is a
          // reassurance, and a false reassurance about pending work is the kind of
          // sentence an officer acts on by closing the tab. `goodNews` is withheld
          // in the same breath — an unproven all-clear is not good news.
          //
          // ⚠ Keyed on `queueProvenComplete`, the SAME predicate as the header, not on
          // `queueMayBeIncomplete`. The two are complements once a response has landed
          // and only then — which is precisely the distinction that produced the
          // header's fail-open — so both sentences read one concept rather than two
          // that happen to agree.
          title={queueProvenComplete ? 'Nothing awaiting a decision' : 'Nothing awaiting a decision in what was loaded'}
          message={
            queueProvenComplete
              ? 'No bill in your scope is submitted, awaiting a level 1 decision, or awaiting a level 2 decision right now.'
              : `None of the first ${loadedBills.length} of ${total} bills in your scope is submitted, awaiting a level 1 decision, or awaiting a level 2 decision. The rest have not been read: use the Bills list, sorted by Status, to page through them.`
          }
          goodNews={queueProvenComplete}
        />
      ) : (
        <>
          <QueueSection
            heading="Awaiting your decision"
            count={mine.length}
            subtitle="You hold the grants these bills need. Every action here writes an audit record against your identity."
            rows={mine}
            onAction={openAction}
            emptyTitle="Nothing here is yours to decide yet"
            emptyMessage={`${assessed.length} ${assessed.length === 1 ? 'bill is' : 'bills are'} awaiting a decision, but none is one your current grants let you make right now. Each is listed below with the reason.`}
          />

          {blockedOnCompute.length > 0 && (
            <QueueSection
              heading="Waiting on a pro-ration split"
              count={blockedOnCompute.length}
              // ⚠ This sentence used to say, flatly, "open a bill to compute it"
              // — while the bill screen told the officer that a decided bill
              // could not be computed at all. For an AWAITING_L2 bill that was
              // never pro-rated, both could not be true, and the officer was
              // sent to a screen that refused them. Both screens now say the
              // same thing, because the bill screen ships the repair: an
              // undecided bill takes an ordinary compute, a decided one takes a
              // level 2 repair, and each is named as what it is.
              subtitle="You could decide these, but they have not been pro-rated yet, and an approval is agreement to a specific split. Open a bill to record the split, then it moves into the section above. On a bill that has already cleared level 1, recording the split is a repair rather than an ordinary compute, and needs level 2 authority."
              rows={blockedOnCompute}
              onAction={openAction}
            />
          )}

          {theirs.length > 0 && (
            <QueueSection
              heading="Awaiting someone else"
              count={theirs.length}
              subtitle="Visible in your scope, but outside what your grants let you decide. Each states why: these are shown rather than hidden, because a bill nobody can see is a bill nobody actions."
              rows={theirs}
              onAction={openAction}
            />
          )}
        </>
      )}

      {pending !== null && (
      <SgdsModal open onSgdsClose={closeAction} size="md">
        <div slot="header">{shown.action === 'REJECT' ? 'Confirm rejection' : `Confirm level ${shown.level} approval`}</div>
        {pending !== null && (
          <div className="flex flex-col gap-3">
            <p>
              {pending.action === 'REJECT' ? 'Reject' : `Approve at level ${pending.level}`}{' '}
              <span className="pt-mono font-semibold pt-ref-link">{pending.bill.reference}</span> for{' '}
              <span className="font-semibold">
                <Money cents={pending.bill.amount} />
              </span>
              ?{' '}
              {pending.action === 'REJECT'
                ? 'This closes the bill. Nothing is paid, and the reason below is written to the transition ledger against your identity.'
                : pending.level === 2
                  ? 'This completes the lifecycle: the bill becomes approved and an audit record is written against your identity.'
                  : 'This moves the bill to level 2 and writes an audit record against your identity.'}
            </p>

            {pending.action === 'REJECT' && (
              <>
                <SgdsTextarea placeholder=""
                  label="Reason for rejection"
                  hintText={rejectReasonHint(reasonMaxLength)}
                  {...(reasonMaxLength === undefined ? {} : { maxlength: reasonMaxLength })}
                  rows={4}
                  required
                  value={pending.reason}
                  onSgdsInput={(e) => setPending((p) => (p === null ? p : { ...p, reason: targetValue(e) }))}
                />
                {/*
                  The field's own message, shown only once something is
                  actually wrong AND the officer has typed — a required-field
                  error that fires on an untouched empty box is noise, and the
                  hint already says the field is required. The Confirm button
                  is disabled either way, so the state is never ambiguous.
                */}
                {pending.reason.length > 0 &&
                  rejectReasonMessage(rejectReasonProblem(pending.reason, reasonMaxLength)) !== null && (
                  <p role="alert" className="text-sm text-[var(--sgds-danger-color-emphasis)]">
                    {rejectReasonMessage(rejectReasonProblem(pending.reason, reasonMaxLength))}
                  </p>
                )}
              </>
            )}

            {/*
              ★★ WAVE 9 — Annex A 37/38's approval COMMENT. OUTSIDE the rejection
              conditional above, deliberately: both stories ask for comments on an
              APPROVAL, which is the case the reason field does not cover, and a
              rejection may carry one too.

              ⚠ It is a SECOND field on a rejection and not a reuse of the reason.
              The two are different records and the ledger keeps them in different
              columns: `reason` is the ground the refusal rests on and is what the
              provider is told; a comment is a remark for whoever reads the history.
              `059_bill_transition_officer_comment.sql` carries the argument.
            */}
            <SgdsTextarea placeholder=""
              label="Comment (optional)"
              hintText={officerCommentHint(commentMaxLength)}
              {...(commentMaxLength === undefined ? {} : { maxlength: commentMaxLength })}
              rows={3}
              value={pending.comment}
              onSgdsInput={(e) => setPending((p) => (p === null ? p : { ...p, comment: targetValue(e) }))}
            />

            {pending.error !== null && (
              <div role="alert" className="pt-well p-3 text-sm text-[var(--sgds-danger-color-emphasis)]">
                {pending.error}
              </div>
            )}
          </div>
        )}
        <div slot="footer" className="flex justify-end gap-2">
          <SgdsButton variant="ghost" type="button" onClick={closeAction}>
            Cancel
          </SgdsButton>
          <SgdsButton
            variant={pending?.action === 'REJECT' ? 'outline' : 'primary'}
            {...(pending?.action === 'REJECT' ? { tone: 'danger' as const } : {})}
            type="button"
            disabled={pending?.action === 'REJECT' && !isSendableRejectReason(pending.reason, reasonMaxLength)}
            loading={pending?.submitting ?? false}
            onClick={submit}
          >
            {shown.action === 'REJECT' ? 'Confirm rejection' : `Confirm level ${shown.level} approval`}
          </SgdsButton>
        </div>
      </SgdsModal>
      )}
    </div>
  )
}

interface Reason {
  readonly label: string | null
  readonly sentence: string
}

/**
 * The reasons a bill offers less than everything, in the officer's language.
 *
 * ⚠ Reported for EVERY unavailable action, including on rows that DO carry a
 * button. The interesting case is the common one: a level 1 officer looking at
 * an `AWAITING_L2` bill gets a Reject button and no Approve button, and without
 * a sentence the missing button just looks like a bug. Same for an uncomputed
 * bill, where Reject is offered and Approve is not.
 *
 * Approve and Reject very often fail for the SAME reason (out of scope, no
 * grants at all), so an identical pair collapses to one unlabelled sentence —
 * the same words twice under two headings read as two separate problems. Where
 * they differ, each is named, because "Approve" and "Reject" being blocked for
 * different reasons is exactly the thing worth saying.
 */
function reasonsFor(assessment: QueueAssessment, level: 1 | 2): readonly Reason[] {
  const { authority, offer } = assessment
  const approveWhy =
    authority.approve !== null && !authority.approve.allowed
      ? unavailabilitySentence(authority.approve.why, level === 2 ? 'APPROVE_L2' : 'APPROVE_L1', formatCents)
      : null
  const rejectWhy =
    authority.reject !== null && !authority.reject.allowed ? unavailabilitySentence(authority.reject.why, 'REJECT', formatCents) : null
  // ⚠ THE SENTENCE THE THIRD SECTION USED TO OWE AND NEVER PAID. A bill blocked
  // only by its missing split says "the split has to be recorded on the bill
  // first" — an instruction — and until now it said that even to an officer who
  // could not record it, with nothing naming who could. `computeUnavailabilitySentence`
  // answers in repair language where the bill needs a repair, which is the
  // vocabulary this case actually needs. Unreachable with today's authority
  // tables (see `lib/queue.ts`) and rendered anyway, because the day it becomes
  // reachable is the day nobody is looking.
  const computeWhy =
    needsSplitRefusal(assessment) && offer !== null && !offer.availability.allowed
      ? computeUnavailabilitySentence(offer.availability.why, offer, formatCents)
      : null

  if (approveWhy !== null && approveWhy === rejectWhy && computeWhy === null) return [{ label: null, sentence: approveWhy }]
  return [
    ...(approveWhy !== null ? [{ label: `Approve (Level ${level})`, sentence: approveWhy }] : []),
    ...(rejectWhy !== null && rejectWhy !== approveWhy ? [{ label: 'Reject', sentence: rejectWhy }] : []),
    ...(computeWhy !== null ? [{ label: 'Recording the split', sentence: computeWhy }] : []),
  ]
}

interface QueueRow {
  readonly bill: Bill
  readonly authority: BillAuthority
  readonly level: 1 | 2
  readonly reasons: readonly Reason[]
}

interface ReasonGroup {
  readonly key: string
  readonly reasons: readonly Reason[]
  readonly rows: readonly QueueRow[]
}

/**
 * Bills sharing an identical set of reasons, gathered under one statement of it.
 *
 * ⚠ Written after seeing the first build on real data: twenty-one bills each
 * carrying the same sixty-word paragraph, one under another. Repetition on that
 * scale stops being an explanation and becomes wallpaper — the reader skims
 * past the third copy and never notices that the eighth row says something
 * different. Said once, over the list it applies to, the differences between
 * groups are what stand out, which is the whole reason the sentences exist.
 *
 * Insertion order is preserved (the server's own bill order decides which group
 * appears first), so this never silently reorders the queue.
 */
function groupByReason(rows: readonly QueueRow[]): readonly ReasonGroup[] {
  const groups = new Map<string, { reasons: readonly Reason[]; rows: QueueRow[] }>()
  for (const row of rows) {
    const key = row.reasons.map((r) => `${r.label ?? ''}|${r.sentence}`).join('||')
    const existing = groups.get(key)
    if (existing === undefined) groups.set(key, { reasons: row.reasons, rows: [row] })
    else existing.rows.push(row)
  }
  return [...groups].map(([key, value]) => ({ key, reasons: value.reasons, rows: value.rows }))
}

function QueueSection({
  heading,
  count,
  subtitle,
  rows,
  onAction,
  emptyTitle,
  emptyMessage,
}: {
  heading: string
  count: number
  subtitle: string
  rows: readonly QueueAssessment[]
  onAction: (bill: Bill, action: PendingAction, level: 1 | 2) => void
  emptyTitle?: string
  emptyMessage?: string
}) {
  const groups = groupByReason(
    rows.map((assessment) => {
      const level: 1 | 2 = assessment.authority.pending === 'LEVEL_2' ? 2 : 1
      return { bill: assessment.bill, authority: assessment.authority, level, reasons: reasonsFor(assessment, level) }
    }),
  )

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-lg">
          {heading} <span className="pt-tabular text-[var(--sgds-color-subtle)]">({count})</span>
        </h2>
        <p className="text-sm text-[var(--sgds-color-subtle)] mt-1 max-w-[70ch]">{subtitle}</p>
      </div>

      {rows.length === 0 ? (
        emptyTitle !== undefined && emptyMessage !== undefined ? (
          <EmptyCard title={emptyTitle} message={emptyMessage} />
        ) : null
      ) : (
        groups.map((group) => (
          <div key={group.key} className="pt-card">
            {group.reasons.length > 0 && (
              <div className="pt-well p-4 mb-2">
                <ul role="list" className="flex flex-col gap-2 max-w-[70ch] m-0 p-0 list-none">
                  {group.reasons.map((reason) => (
                    <li key={reason.label ?? 'both'} className="text-sm">
                      {reason.label !== null && <strong className="font-semibold">{reason.label} is not available to you. </strong>}
                      <span className="text-[var(--sgds-body-color-subtle)]">{reason.sentence}</span>
                    </li>
                  ))}
                </ul>
                {/*
                  `--sgds-body-color-subtle`, not `--sgds-color-subtle`.
                  Measured on the dark well this line sits in, the latter came
                  back at **4.55:1** — it passes AA at 14px, but by 0.05, which
                  is not a margin. The body-subtle token measures **6.56:1** on
                  the same surface (7.04:1 in light) and is what the sentence
                  beside it already uses.
                */}
                <p className="text-sm text-[var(--sgds-body-color-subtle)] mt-3">
                  {group.rows.length === 1 ? 'One bill below.' : `${group.rows.length} bills below.`}
                </p>
              </div>
            )}
            <div className="flex flex-col">
              {group.rows.map((row) => (
                <QueueBillRow key={row.bill.reference} row={row} onAction={onAction} />
              ))}
            </div>
          </div>
        ))
      )}
    </section>
  )
}

function QueueBillRow({ row, onAction }: { row: QueueRow; onAction: (bill: Bill, action: PendingAction, level: 1 | 2) => void }) {
  const { bill, authority, level } = row
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap py-3 border-t border-[var(--sgds-border-color-muted)] first:border-t-0">
      <div className="min-w-0">
        <Link to={`/bills/${bill.reference}`} className="pt-mono font-semibold pt-ref-link">
          {bill.reference}
        </Link>
        <div className="text-sm text-[var(--sgds-color-subtle)]">
          {bill.agencyCode} · {billTypeLabel(bill.billType)} · service date {formatIsoDate(bill.serviceDate)}
        </div>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <span className="pt-tabular font-semibold text-lg">
          <Money cents={bill.amount} />
        </span>
        <BillStatusBadge status={bill.status} />
        {authority.hasAnyAction && (
          <div className="flex items-center gap-2">
            {authority.approve?.allowed === true && (
              <SgdsButton variant="primary" size="sm" type="button" onClick={() => onAction(bill, 'APPROVE', level)}>
                Approve (Level {level})
              </SgdsButton>
            )}
            {authority.reject?.allowed === true && (
              <SgdsButton variant="outline" tone="danger" size="sm" type="button" onClick={() => onAction(bill, 'REJECT', level)}>
                Reject
              </SgdsButton>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
